import { forwardRef, memo, useEffect, useRef, useState } from "react";
import {
    createRecordingTicket,
    FleetMissionRunRecording,
} from "lib/fleetMissionStorage/FleetMissionStorageClient";

type FleetRunRecordingProps = {
    proxyHostname: string | null;
    sourceArcSerial: string;
    sourceRunId: string;
    recording: FleetMissionRunRecording | null;
    /** True while the run-details request that supplies `recording` is pending. */
    metadataLoading?: boolean;
    className?: string;
};

const IN_PROGRESS_RECORDING_STATUSES = new Set(["waiting_source", "pending", "fetching"]);

const FleetRunRecording = forwardRef<HTMLVideoElement, FleetRunRecordingProps>(
    ({ proxyHostname, sourceArcSerial, sourceRunId, recording, metadataLoading = false, className }, ref) => {
        const [url, setUrl] = useState<string | null>(null);
        const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
        const refreshAttempted = useRef(false);

        const loadTicket = async (signal?: AbortSignal) => {
            if (metadataLoading || (recording && !recording.fetchStatus)) {
                setUrl(null);
                setState("loading");
                return;
            }
            if (!recording?.available || recording.fetchStatus !== "stored") {
                setUrl(null);
                setState(recording ? "idle" : "error");
                return;
            }
            setState("loading");
            try {
                const ticket = await createRecordingTicket(
                    proxyHostname,
                    sourceArcSerial,
                    sourceRunId,
                    signal,
                );
                if (!ticket) {
                    setUrl(null);
                    setState("error");
                    return;
                }
                setUrl(ticket.url);
                setState("ready");
            } catch (error) {
                if ((error as { name?: string })?.name !== "AbortError") {
                    setUrl(null);
                    setState("error");
                }
            }
        };

        useEffect(() => {
            refreshAttempted.current = false;
            const controller = new AbortController();
            void loadTicket(controller.signal);
            return () => controller.abort();
            // The recording metadata is the polling boundary; do not reload
            // the ticket merely because an unrelated log event changed.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            proxyHostname,
            sourceArcSerial,
            sourceRunId,
            recording?.available,
            recording?.fetchStatus,
            metadataLoading,
        ]);

        const handleError = () => {
            if (refreshAttempted.current) {
                setState("error");
                return;
            }
            refreshAttempted.current = true;
            const controller = new AbortController();
            void loadTicket(controller.signal);
        };

        const recordingIsLoading = metadataLoading
            || Boolean(
                recording
                && (
                    !recording.fetchStatus
                    || IN_PROGRESS_RECORDING_STATUSES.has(recording.fetchStatus)
                ),
            );
        if (recordingIsLoading || state === "loading") {
            return (
                <div className="flex aspect-video w-full max-h-full items-center justify-center rounded-r10 border border-black70 bg-black100 text-h5 text-black40">
                    Loading
                </div>
            );
        }
        if (state === "error" || !url || recording?.fetchStatus !== "stored") {
            return (
                <div className="flex aspect-video w-full max-h-full items-center justify-center rounded-r10 border border-black70 bg-black100 text-h5 text-black40">
                    Recording unavailable
                </div>
            );
        }
        return (
            <video
                ref={ref}
                key={url}
                src={url}
                className={className}
                onError={handleError}
                controls={false}
            />
        );
    },
);

FleetRunRecording.displayName = "FleetRunRecording";

export default memo(FleetRunRecording);
